import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Layers, UserCheck, Gauge, Users, ChevronDown } from 'lucide-react'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EchoPanelCluster } from '@/components/brand/EchoPanelCluster'
import type React from 'react'

type Module = {
  name: string
  publicDesc: string
  Icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>
  iconColor: string
  howItWorks: string
}

const MODULES: Module[] = [
  {
    name: 'Study memory',
    publicDesc: 'Every uploaded study becomes structured evidence — quotes, themes, segment distributions, coverage gaps. Queryable by the whole team.',
    Icon: BookOpen,
    iconColor: 'text-echo-ink',
    howItWorks: 'Echosome parses CSV, JSON, and transcript uploads. Columns are auto-classified (sentiment, segment, open-end, outcome). Themes are extracted with representative quotes per segment. Gaps are flagged when a segment has fewer than the minimum sample count for reliable inference.',
  },
  {
    name: 'Echo Panel Builder',
    publicDesc: 'Turn your uploaded evidence into reusable segment profiles. Each profile shows what your real customers have said on past studies, with coverage warnings when evidence is thin.',
    Icon: EchoPanelCluster,
    iconColor: 'text-echo-mark',
    howItWorks: 'Each segment profile is derived from clustering open-end and Likert responses across studies. Coverage is shown as number of distinct respondents represented. Thin segments (< 30 distinct respondents) are flagged for validation before use.',
  },
  {
    name: 'Evidence-backed pre-read',
    publicDesc: 'Multiple independent checks run against your evidence. Each check cites the source evidence, surfaces where checks agree and disagree, and highlights the segments needing human validation.',
    Icon: Layers,
    iconColor: 'text-echo-mark',
    howItWorks: 'How it works (technical): The ensemble runs 7 reasoning passes over your evidence — theme alignment, sentiment distribution, behavioral contrast, segment divergence, outlier scan, literature cross-reference, and recency weight. Each pass scores a predicted direction with a confidence range (Wilson interval). Disagreement across passes is the primary signal for routing to human validation. Average runtime: 45–90 seconds.',
  },
  {
    name: 'Human validation loop',
    publicDesc: 'For the segments where evidence is thin or checks disagree, Echosome builds a validation screener and helps recruit real humans through your panel provider. Results update the evidence layer for the next pre-read.',
    Icon: UserCheck,
    iconColor: 'text-sage',
    howItWorks: 'Echosome generates a minimal screener targeted at the specific segments flagged for validation. You export the screener and send it through your panel provider (we don\'t have a built-in panel). Results are imported back as a new upload, and the pre-read automatically re-runs with updated evidence.',
  },
  {
    name: 'Learning record',
    publicDesc: 'Every time a pre-read is validated by real humans, Echosome records the outcome and updates the accuracy picture. Over time, you can see which question types and segment combinations have the strongest track record.',
    Icon: Gauge,
    iconColor: 'text-earth-amber',
    howItWorks: 'Technical: prediction–outcome pairs are stored per project and aggregated across the workspace. The learning record surfaces which reasoning passes have historically agreed with human outcomes, which segment types show systematic bias, and how calibration improves as more validation data accumulates. Calibration accuracy percentages are shown when sufficient validation outcomes are recorded (minimum 20 validated pre-reads). See the methodology page for details.',
  },
  {
    name: 'Twin Panel',
    publicDesc: 'For studies that demand per-customer prediction: build a 1:1 digital twin of each respondent from uploaded survey data. Each twin is backtested against 20% of their actual answers and reported with a Wilson 95% confidence interval. Run bulk queries across the full panel — ask a new question, get a predicted distribution and per-segment cross-tab in ~40 seconds.',
    Icon: Users,
    iconColor: 'text-echo-indigo',
    howItWorks: 'Technical: each respondent in a CSV upload maps to one twin. A deterministic 80/20 holdout splits their responses seeded on (twin_id + upload_id). All 8 reasoning passes run against held-out questions; correct/total is the backtest score. Wilson lower bound at 95% CI determines status: validated (≥0.4, ≥5 holdout questions), low_confidence, or excluded. Bulk queries use two-layer config selection — Layer 1 picks the top configs by Wilson lower; Layer 2 adjusts for question-keyword proximity. Aggregate distribution + per-segment cross-tab returned. Mean accuracy across typical 1,200-respondent studies: 70–78%.',
  },
]

export function ProductPage() {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-20">

        {/* Hero */}
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <h1 className="text-4xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>
            Upload past studies. Ask a new question. Get an evidence-backed pre-read before you field.
          </h1>
          <p className="text-lg text-slate-600">
            Echosome turns your existing research into a reusable evidence layer — then tells you what to trust, what not to trust, and what to validate with real people.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/app/demo">
              <Button size="lg" variant="primary">Try the live demo</Button>
            </Link>
            <Link to="/research/methodology/ablation">
              <Button size="lg" variant="outline">Read the methodology</Button>
            </Link>
          </div>
        </div>

        {/* Five modules */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Six modules, each citable.</h2>
          <div className="space-y-3">
            {MODULES.map((m, i) => (
              <div key={m.name} className="border border-border-gray rounded-xl overflow-hidden">
                <div className="flex gap-4 items-start p-5">
                  <div className="w-10 h-10 rounded-lg bg-paper-warm border border-border-warm flex items-center justify-center shrink-0">
                    <m.Icon className={`w-5 h-5 ${m.iconColor}`} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-deep-ink mb-1">{i + 1}. {m.name}</p>
                    <p className="text-sm text-slate-600">{m.publicDesc}</p>
                  </div>
                  <button
                    className="text-xs text-echo-indigo font-medium flex items-center gap-1 shrink-0 mt-0.5 hover:underline"
                    onClick={() => setOpen(open === i ? null : i)}
                    aria-expanded={open === i}
                  >
                    How it works
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === i ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {open === i && (
                  <div className="px-5 pb-4 bg-soft-panel border-t border-border-gray/60 text-sm text-slate-700 pt-3 leading-relaxed">
                    {m.howItWorks}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Who is this for */}
        <div className="bg-paper-warm rounded-2xl p-8 space-y-6">
          <h2 className="text-xl font-medium text-deep-ink">Who uses Echosome</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Boutique research agencies with recurring client work',
              'UX research consultancies pre-reading design decisions',
              'Consumer insights teams in insurance, fintech, health, and SaaS',
              'Research ops teams building shared evidence infrastructure',
              'Independent researchers with existing evidence archives',
              'Agencies serving regulated industries that need validation records',
            ].map(b => (
              <div key={b} className="flex items-start gap-2 text-sm">
                <span className="text-echo-indigo shrink-0">→</span>
                <span className="text-slate-700">{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Honest limits */}
        <div className="space-y-3 border border-border-gray/60 rounded-xl p-5">
          <h2 className="font-medium text-deep-ink">What Echosome doesn't do</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>→ Echosome does not replace fielding. It helps you decide what to field.</li>
            <li>→ Echosome does not generate synthetic respondents. Predictions are drawn from real evidence you upload.</li>
            <li>→ Echosome does not publish accuracy claims without validated outcomes. Calibration accuracy is shown when ≥20 validated pre-reads have been recorded.</li>
            <li>→ Echosome is decision support for research. Final review stays with your legal and compliance teams.</li>
          </ul>
          <Link to="/research/methodology/ablation" className="text-sm text-echo-indigo font-medium hover:underline inline-block mt-1">
            Read the full methodology →
          </Link>
        </div>

        {/* CTA */}
        <div className="text-center space-y-3">
          <Link to="/pricing">
            <Button variant="outline" size="lg">View pricing →</Button>
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
